import cn from "classnames"
import React, { useEffect, useState } from "react"
import { useToggle } from "react-use"

import style from "./style.module.scss"
import { ButtonsGroup } from "./ButtonsGroup"
import { ReferralB2b } from "./ReferralB2b/ReferralB2b"
import { ReferralB2c } from "./ReferralB2c"

import { Box, Button, Img, Input, Text } from "ui-kit/components-branded/index"
import { Tooltip } from "components"
import { Analytics } from "utils/analytics"
import { createRefShareLink } from "modules/ClientAccount/utils"
import { useGetRefShareLink, useScrollByUrlHash } from "modules/ClientAccount/hooks"
import { useClientAccountContext } from "modules/ClientAccount/Context"
import { notificationCopyUrl } from "utils/helpers"
import { useClientContext } from "modules/ClientAuthorization/common/ClientContext"
import { APP_PAGE_URL } from "utils/env-management"

const tooltipList = [
    <>Скопируйте ссылку с&nbsp;промокодом и&nbsp;отправьте ее&nbsp;другу.</>,
    <>Друг получит скидку&nbsp;10% на&nbsp;первые 4&nbsp;сессии с&nbsp;любым психологом.</>,
    <>
        Как только друг пройдет первую сессию с&nbsp;психологом, мы&nbsp;пришлем вашу
        скидку&nbsp;10% на&nbsp;4&nbsp;сессии.
    </>,
    <>
        По&nbsp;вашему промокоду может записаться любое количество людей&nbsp;&mdash;
        вы&nbsp;получите скидки за&nbsp;всех. Скидки не&nbsp;суммируются.
    </>,
]

const tooltipListForB2b = [
    <>Отправьте ссылку с&nbsp;промокодом&nbsp;другу.</>,
    <>Друг получит скидку&nbsp;30% на&nbsp;первые 3&nbsp;сессии c&nbsp;любым психологом.</>,
    <>Скидки не суммируются между собой и&nbsp;с&nbsp;другими скидками, включая корпоративные.</>,
    <>По&nbsp;вашему промокоду может записаться любое количество людей.</>,
]

interface Props {
    className?: string
    source: "payment-succeed" | "lk"
}

export function ReferralLinkWidget(props: Props) {
    useScrollByUrlHash()
    const [url, setUrl] = useState<string>()
    const { data: refData } = useGetRefShareLink()
    const [isLoading, toggleIsLoading] = useToggle(false)
    const [, { toggleShowRefWidget }] = useClientAccountContext()
    const [{ userAccount: clientUserAccount }] = useClientContext()
    const isB2b = clientUserAccount.is_b2b

    useEffect(() => {
        refData && setUrl(refData.url)
    }, [refData])

    function handleClickButton() {
        Analytics.ref_lk_clicked()
        toggleIsLoading()
        void createRefShareLink()
            .then(({ url }) => {
                setUrl(url)
            })
            .finally(() => {
                toggleIsLoading()
            })
    }

    function handleClickCopyLinkButton() {
        if (url) {
            navigator.clipboard
                .writeText(url)
                .then(() => {
                    toggleShowRefWidget && toggleShowRefWidget(false)
                    notificationCopyUrl()
                    Analytics.ref_promo_copied()
                })
                .catch(() => undefined)
        }
    }

    const isShowB2b = isB2b
    const isShowB2c = !isB2b && clientUserAccount.allowed_referral_program

    if (!(isShowB2b || isShowB2c)) return null

    return (
        <Box className={props.className} id="get-ref">
            <div className={cn("is-flex is-justify-content-space-between mb-3", style.content)}>
                {isShowB2b && <ReferralB2b />}
                {isShowB2c && <ReferralB2c />}
                <div className={style.image}>
                    <Img src="talk.svg" />
                </div>
            </div>
            <hr className="mgb-3 mgt-3" />
            <Text isAccent className="mgb-3 is-inline-block">
                Отправьте ссылку
            </Text>
            {!url && (
                <Button
                    color="primary"
                    className="mb-3"
                    isFullwidth
                    onClick={handleClickButton}
                    isLoading={isLoading}
                >
                    Создать ссылку
                </Button>
            )}
            {url && (
                <>
                    <div className="is-flex mb-3">
                        <div className="flex-grow-1 mgr-4">
                            <Input className="client-account__disabled-input" defaultValue={url} />
                        </div>
                        <Button color="primary" onClick={handleClickCopyLinkButton}>
                            Скопировать
                        </Button>
                    </div>
                    <ButtonsGroup
                        urlForShare={url}
                        shareTitle={isB2b ? TITLE_FOR_B2B : TITLE}
                        className="mgb-3"
                    />
                </>
            )}
            <Text
                className={isB2b ? cn("mgb-3", style.info, "is-block") : cn(style.info, "is-block")}
            >
                <Tooltip
                    overlay={
                        <>
                            <ul className="list-disc ml-0 pl-3 sb-3 mb-2">
                                {(isB2b ? tooltipListForB2b : tooltipList).map((item, i) => {
                                    return (
                                        <li key={i}>
                                            <Text>{item}</Text>
                                        </li>
                                    )
                                })}
                            </ul>
                        </>
                    }
                    placement="topLeft"
                    className={style.tooltip}
                >
                    <span className={style.button}>Как это работает ?</span>
                </Tooltip>
            </Text>
            <Text isSecondary className={style.contractOfferText}>
                Копируя или&nbsp;отправляя ссылку, вы&nbsp;тем&nbsp;самым подтверждаете,
                что&nbsp;ознакомились, полностью согласны и&nbsp;приняли{" "}
                <a
                    href={`${APP_PAGE_URL}/${isB2b ? "discount_conditions_b2b" : "referalrules"}`}
                    target="_blank"
                    className={style.contractOfferTextLink}
                    rel="noreferrer"
                >
                    Правила Акции.
                </a>
            </Text>
        </Box>
    )
}

const TITLE = encodeURI("Друг подарил вам скидку 10% на 4 сессии в Alter")

const TITLE_FOR_B2B = encodeURI("Друг подарил вам скидку 30% на 3 сессии в Alter")
